import DataTable from "../components/Table/DataTable";
import { useEffect, useState, useCallback } from "react";
import {
  createClient,
  getAllClients,
  updateClient,
} from "../services/clients/client.api";
import { getAllProviders } from "../services/providers/provider.api";
import { getAllServerUrls } from "../services/serverUrls/serverUrl.api";
import { clientColumns } from "../types/clientTypes";
import { ClientForm } from "../shared/forms/clientForm";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
} from "@mui/material";
import { customAlphabet } from "nanoid";
import { toast } from "../components/Toast/Toast";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

export default function Dashboard() {
  const [allClients, setAllClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [clientId, setClientId] = useState("");
  const [editingClient, setEditingClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [providers, setProviders] = useState([]);
  const [serverUrls, setServerUrls] = useState([]);
  const [isActivationModalOpen, setIsActivationModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  let credentials = `
Please initiate an integration for the TP

TP: ${selectedClient?.name || "N/A"}

OAUTH URL: https://oauth.nemtpanel.com/oauth/token

client_id: ${selectedClient?.clientId || "N/A"}
client_secret: ${selectedClient?.clientSecret || "N/A"}
grant_type: client_credentials
scope: api
  `.trim();

  const fetchProviders = useCallback(async () => {
    await getAllProviders({
      page: 1,
      limit: 1000,
    })
      .then((response) => {
        setProviders(response.data);
      })
      .catch((err) => {
        toast.error(err.response?.data?.error || "Failed to fetch providers");
      });
  }, []);

  const fetchServerUrls = useCallback(async () => {
    await getAllServerUrls({
      page: 1,
      limit: 1000,
    })
      .then((response) => {
        setServerUrls(response.data);
      })
      .catch((err) => {
        toast.error(err.response?.data?.error || "Failed to fetch server urls");
      });
  }, []);

  useEffect(() => {
    fetchProviders();
    fetchServerUrls();
  }, [fetchProviders, fetchServerUrls]);

  const fetchClients = useCallback(() => {
    setLoading(true);
    getAllClients({ page: page + 1, limit: rowsPerPage })
      .then((res) => {
        setAllClients(res.data);
        setTotalCount(res.totalResults);
      })
      .catch((err) => {
        toast.error("Failed to fetch clients");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleOpen = () => {
    //debugger
    const alphabet = "0123456789abcdef";
    const generateClientId = customAlphabet(alphabet, 45);
    // const generateSecret = customAlphabet(alphabet, 42);

    setClientId(generateClientId());
    // setClientSecret(generateSecret());
    setEditingClient(null);
    setOpen(true);
  };

  const handleEdit = (client) => {
    debugger
    setEditingClient(client);
    setOpen(true);
  };

  const handleActivate = (client) => {
    setSelectedClient(client);
    setIsActivationModalOpen(true);
  };

  const handleCreateOrEdit = async (client) => {
    debugger
    const data = { ...client };
    if (editingClient) {
      await updateClient(editingClient._id, data)
        .then((res) => {
          setAllClients(allClients.map((c) => (c._id === res._id ? res : c)));
          setOpen(false);
          setEditingClient(null);
          toast.success("Client updated successfully!");
        })
        .catch((err) => {
          toast.error(err.response?.data?.error || "Failed to update Client");
        });
    } else {
      debugger
      await createClient(data)
        .then((res) => {
          setAllClients((prev) => {
            const updated = [res, ...prev];
            if (updated.length > rowsPerPage) {
              updated.pop();
            }
            return updated;
          });
          setTotalCount((prev) => prev + 1);
          setOpen(false);
          toast.success("Client created successfully!");
        })
        .catch((err) => {
          toast.error(err.response?.data?.error || "Failed to create Client");
        });
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(credentials);
    toast.success("Template message copied to clipboard!");
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div className="px-6">
      <div className="flex justify-end items-center mb-2">
        <Button
          variant="contained"
          onClick={handleOpen}
          sx={{ textTransform: "none", fontWeight: "bold", fontSize: "14px" }}
        >
          Add
        </Button>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <DataTable
          columns={clientColumns}
          rows={allClients}
          onEdit={handleEdit}
          onActivate={handleActivate}
          loading={loading}
          totalCount={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </div>
      <ClientForm
        open={open}
        onClose={() => {
          setOpen(false);
          setEditingClient(null);
        }}
        handleCreateOrEdit={handleCreateOrEdit}
        clientId={clientId}
        providers={providers}
        serverUrls={serverUrls}
        client={editingClient}
      />
      <Dialog
        open={isActivationModalOpen}
        onClose={() => {
          setIsActivationModalOpen(false);
          setSelectedClient(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "primary.main" }}>
          Activation Credentials
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Message:
            </Typography>
            <Box
              sx={{
                p: 1,
                bgcolor: "grey.50",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "grey.200",
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
              }}
            >
              {credentials}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "space-end" }}>
          <Button
            onClick={() => {
              setIsActivationModalOpen(false);
              setSelectedClient(null);
            }}
            variant="outlined"
            sx={{
              textTransform: "none",
            }}
          >
            Close
          </Button>
          <Button
            onClick={handleCopy}
            startIcon={<ContentCopyIcon />}
            variant="outlined"
            color="primary"
            sx={{
              textTransform: "none",
              bgcolor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            Copy
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
